import { useCallback, useEffect, useReducer, useRef, useState } from "react"

import { ifeUrl } from "shared/lib/ife_url"
import { streamSSE, type SSEEvent } from "shared/lib/sse_client"
import { clearAnalytics, readAnalytics, writeAnalytics } from "./analytics_storage"
import type {
  AssistantItem,
  CapWarning,
  ChatItem,
  Phase,
  SessionExitMethod,
  StoredSession,
  ToolItem,
  TurnUserAction,
  UIEvent,
  UseChatOptions,
  UseChatReturn,
} from "./types"

const DEFAULT_STORAGE_KEY = "conversation:lastSession"
const defaultEndpoint = () => ifeUrl("/chat/messages")
const defaultExportEndpoint = (sessionId: string) => ifeUrl(`/chat/sessions/${encodeURIComponent(sessionId)}/export`)

const RECOVERABLE_ERROR_CODES = new Set([
  "turn_in_progress",
  "internal_error",
  "bedrock_throttled",
  "bedrock_timeout",
  "bedrock_unavailable",
])
const TERMINAL_ERROR_CODES = new Set(["session_full", "token_cap_exceeded"])

const APPLET_TOOL_NAMES = new Set(["CreateApplet", "create_applet"])

// Permission cards that stay in the thread as a disabled record of the user's choice, rather than
// being replaced by a user bubble. The agent still receives the same ui_response either way.
const SETTLING_CARD_TYPES = new Set(["service_connection", "upgrade_required", "applet_preview_card"])

type State = {
  items: ChatItem[]
  phase: Phase
  sessionId: string | null
  capWarning: CapWarning | null
  itemSeq: number
}

type Action =
  | { type: "RESET" }
  | { type: "CONNECTING" }
  | { type: "USER_SEND"; text: string }
  | { type: "SESSION"; sessionId: string }
  | { type: "STATUS" }
  | { type: "TOKEN"; text: string }
  | { type: "TOOL_START"; toolId: string; tool: string }
  | { type: "TOOL_DONE"; toolId: string; status?: string; error?: string }
  | { type: "UI_EVENT"; ui: UIEvent }
  | { type: "UI_RESPOND"; uiId: string; displayText?: string; ingredients?: Record<string, string> }
  | { type: "UI_SETTLE"; uiId: string; actionId: string; displayText?: string }
  | { type: "UI_DISMISS"; uiId: string }
  | { type: "WARNING"; usage: number; cap: number }
  | { type: "ERROR_NOTICE"; code: string; message: string; recoverable: boolean }
  | { type: "DONE" }
  | { type: "FINALIZE_INTERRUPTED" }
  | { type: "HYDRATE"; items: ChatItem[]; sessionId: string }

const INITIAL_STATE: State = {
  items: [],
  phase: "idle",
  sessionId: null,
  capWarning: null,
  itemSeq: 0,
}

function nextId(state: State): { id: string; seq: number } {
  const seq = state.itemSeq + 1
  return { id: `m_${seq}`, seq }
}

function finalizeStreamingAssistant(items: ChatItem[]): ChatItem[] {
  if (items.length === 0) return items
  const last = items[items.length - 1]
  if (last.kind !== "assistant" || last.status !== "streaming") return items
  return [...items.slice(0, -1), { ...last, status: "done" }]
}

function reducer(state: State, action: Action): State {
  switch (action.type) {
    case "RESET":
      return INITIAL_STATE
    case "HYDRATE":
      return {
        ...INITIAL_STATE,
        items: action.items,
        sessionId: action.sessionId,
        itemSeq: action.items.length,
        phase: "done",
      }
    case "CONNECTING":
      return { ...state, phase: "connecting" }
    case "USER_SEND": {
      const { id, seq } = nextId(state)
      return { ...state, itemSeq: seq, items: [...state.items, { kind: "user", id, text: action.text }] }
    }
    case "SESSION": {
      const phase: Phase = state.phase === "connecting" ? "thinking" : state.phase
      return { ...state, sessionId: action.sessionId, phase }
    }
    case "STATUS":
      return { ...state, phase: "thinking" }
    case "TOKEN": {
      const last = state.items[state.items.length - 1]
      if (last && last.kind === "assistant" && last.status === "streaming") {
        const updated: AssistantItem = { ...last, text: last.text + action.text }
        return { ...state, phase: "streaming", items: [...state.items.slice(0, -1), updated] }
      }
      const { id, seq } = nextId(state)
      const fresh: AssistantItem = { kind: "assistant", id, text: action.text, status: "streaming" }
      return { ...state, itemSeq: seq, phase: "streaming", items: [...state.items, fresh] }
    }
    case "TOOL_START": {
      // Collapse a redundant duplicate when the backend emits a second tool_start for
      // the tool already shown in the latest row. Adjacency-scoped: any intervening
      // assistant text or card means it's a distinct step and gets its own row.
      const last = state.items[state.items.length - 1]
      if (last && last.kind === "tool" && last.tool === action.tool) {
        const items = [...state.items]
        if (last.phase === "start") {
          items[items.length - 1] = { ...last, toolId: action.toolId }
          return { ...state, phase: "tool_running", items }
        }
        items[items.length - 1] = { ...last, toolId: action.toolId, phase: "start", status: undefined, error: undefined }
        return { ...state, phase: "tool_running", items }
      }
      const { id, seq } = nextId(state)
      const item: ToolItem = { kind: "tool", id, toolId: action.toolId, tool: action.tool, phase: "start" }
      return { ...state, itemSeq: seq, phase: "tool_running", items: [...state.items, item] }
    }
    case "TOOL_DONE": {
      let foundIdx = -1
      for (let i = state.items.length - 1; i >= 0; i--) {
        const it = state.items[i]
        if (it.kind === "tool" && it.phase === "start" && (it.toolId === action.toolId || it.tool === action.toolId)) {
          foundIdx = i
          break
        }
      }
      if (foundIdx === -1) return state
      const prev = state.items[foundIdx] as ToolItem
      const updated: ToolItem = { ...prev, phase: "done", status: action.status, error: action.error }
      const items = [...state.items]
      items[foundIdx] = updated
      const stillRunning = items.some(it => it.kind === "tool" && it.phase === "start")
      const phase: Phase = stillRunning ? "tool_running" : "thinking"
      return { ...state, items, phase }
    }
    case "UI_EVENT": {
      const { id, seq } = nextId(state)
      return { ...state, itemSeq: seq, items: [...state.items, { kind: "ui", id, ui: action.ui }] }
    }
    case "UI_RESPOND": {
      const withoutCard = state.items.filter(it => !(it.kind === "ui" && it.ui.id === action.uiId))
      if (!action.displayText) {
        return { ...state, phase: "connecting", items: withoutCard }
      }
      const { id, seq } = nextId(state)
      return {
        ...state,
        itemSeq: seq,
        phase: "connecting",
        items: [...withoutCard, { kind: "user", id, text: action.displayText, ingredients: action.ingredients }],
      }
    }
    case "UI_SETTLE": {
      // Keep the card in place, disabled, recording the choice — no user bubble is inserted.
      const items = state.items.map(it =>
        it.kind === "ui" && it.ui.id === action.uiId
          ? { ...it, response: { actionId: action.actionId, displayText: action.displayText } }
          : it
      )
      return { ...state, phase: "connecting", items }
    }
    case "UI_DISMISS":
      return { ...state, items: state.items.filter(it => !(it.kind === "ui" && it.ui.id === action.uiId)) }
    case "WARNING":
      return { ...state, capWarning: { usage: action.usage, cap: action.cap } }
    case "ERROR_NOTICE": {
      const { id, seq } = nextId(state)
      const isTerminal = TERMINAL_ERROR_CODES.has(action.code)
      return {
        ...state,
        itemSeq: seq,
        items: [
          ...finalizeStreamingAssistant(state.items),
          { kind: "notice", id, severity: "error", code: action.code, message: action.message, recoverable: action.recoverable },
        ],
        phase: "done",
        sessionId: isTerminal ? null : state.sessionId,
      }
    }
    case "DONE":
      return { ...state, phase: "done", items: finalizeStreamingAssistant(state.items) }
    case "FINALIZE_INTERRUPTED":
      return { ...state, phase: "done", items: finalizeStreamingAssistant(state.items) }
    default:
      return state
  }
}

function readStored(storage: UseChatOptions["storage"], key: string): StoredSession | null {
  if (!storage) return null
  try {
    const raw = storage.getItem(key)
    if (!raw) return null
    const parsed = JSON.parse(raw) as Partial<StoredSession>
    if (!parsed.sessionId || !parsed.intention) return null
    return {
      sessionId: parsed.sessionId,
      intention: parsed.intention,
      settled: Boolean(parsed.settled),
    }
  } catch {
    return null
  }
}

function writeStored(storage: UseChatOptions["storage"], key: string, value: StoredSession): void {
  if (!storage) return
  try {
    storage.setItem(key, JSON.stringify(value))
  } catch {
    // best-effort
  }
}

function clearStored(storage: UseChatOptions["storage"], key: string): void {
  if (!storage) return
  try {
    storage.removeItem(key)
  } catch {
    // best-effort
  }
}

// Drops the session-resume pointer and its analytics snapshot together, so the next
// Conversation mount starts a fresh session instead of resuming an unsettled one
// (e.g. when a Try it prefill arrives). Keeping the pair in one place means a future
// storage key gets cleared everywhere by updating only this function and reset().
export function clearStoredConversation(storage: UseChatOptions["storage"]): void {
  clearStored(storage, DEFAULT_STORAGE_KEY)
  if (storage) clearAnalytics(storage)
}

function pickString(obj: Record<string, unknown>, ...keys: string[]): string | undefined {
  for (const key of keys) {
    const val = obj[key]
    if (typeof val === "string" && val.length > 0) return val
  }
  return undefined
}

function pickNumber(obj: Record<string, unknown>, ...keys: string[]): number | undefined {
  for (const key of keys) {
    const val = obj[key]
    if (typeof val === "number" && Number.isFinite(val)) return val
  }
  return undefined
}

type ParsedHistory = { items: ChatItem[]; settled: boolean }

function parseHistoryExport(data: unknown): ParsedHistory {
  if (!data || typeof data !== "object") return { items: [], settled: true }
  const obj = data as Record<string, unknown>
  const messages = Array.isArray(obj.messages) ? obj.messages : []
  const items: ChatItem[] = []
  let lastWasAssistant = false

  const textOf = (m: Record<string, unknown>): string =>
    (Array.isArray(m.content) ? m.content : [])
      .map(c => (c && typeof c === "object" ? String((c as Record<string, unknown>).text || "") : ""))
      .join("")

  // First pass: map each card to its type, and collect the choice from a later ui_response (its
  // action and the user turn's text). Cards answered this way must not re-render as live on resume.
  const uiTypeById = new Map<string, string>()
  const responseByUiId = new Map<string, { actionId: string; displayText: string }>()
  for (const msg of messages) {
    if (!msg || typeof msg !== "object") continue
    const m = msg as Record<string, unknown>
    for (const ui of Array.isArray(m.ui_events) ? (m.ui_events as UIEvent[]) : []) {
      if (ui && typeof ui.id === "string") uiTypeById.set(ui.id, ui.type)
    }
    const resp = m.ui_response
    if (resp && typeof resp === "object") {
      const uiId = (resp as Record<string, unknown>).ui_id
      const actionId = (resp as Record<string, unknown>).action_id
      if (typeof uiId === "string") {
        responseByUiId.set(uiId, { actionId: typeof actionId === "string" ? actionId : "", displayText: textOf(m) })
      }
    }
  }
  const answeredUiIds = new Set(responseByUiId.keys())
  // Permission cards persist as a settled, disabled record; the user turn that answered them is
  // suppressed so it isn't shown twice.
  const settledUiIds = new Set([...answeredUiIds].filter(id => SETTLING_CARD_TYPES.has(uiTypeById.get(id) ?? "")))

  for (const msg of messages) {
    if (!msg || typeof msg !== "object") continue
    const m = msg as Record<string, unknown>
    const role = typeof m.role === "string" ? m.role : ""
    const text = textOf(m)

    if (role === "user" && text) {
      const resp = m.ui_response
      const uiId = resp && typeof resp === "object" ? (resp as Record<string, unknown>).ui_id : undefined
      if (typeof uiId === "string" && settledUiIds.has(uiId)) continue
      items.push({ kind: "user", id: `h_${items.length}`, text })
      lastWasAssistant = false
    } else if (role === "assistant") {
      if (text) {
        items.push({ kind: "assistant", id: `h_${items.length}`, text, status: "done" })
        lastWasAssistant = true
      }
      const uiEvents = Array.isArray(m.ui_events) ? (m.ui_events as UIEvent[]) : []
      for (const ui of uiEvents) {
        if (settledUiIds.has(ui.id)) {
          const { actionId, displayText } = responseByUiId.get(ui.id)!
          items.push({ kind: "ui", id: `h_${items.length}`, ui, response: { actionId, displayText } })
          continue
        }
        if (answeredUiIds.has(ui.id)) continue
        items.push({ kind: "ui", id: `h_${items.length}`, ui })
      }
    }
  }

  return { items, settled: lastWasAssistant }
}

type SessionAnalytics = {
  sessionId: string | null
  startedAt: number | null
  turnsStarted: number
  aiResponses: number
  goalAchieved: boolean
  errorBlocking: boolean
  started: boolean
  ended: boolean
}

function freshAnalytics(): SessionAnalytics {
  return {
    sessionId: null,
    startedAt: null,
    turnsStarted: 0,
    aiResponses: 0,
    goalAchieved: false,
    errorBlocking: false,
    started: false,
    ended: false,
  }
}

type PendingTurn = {
  turnNumber: number
  promptAt: number
  responseTimeMs: number | null
  errorOccurred: boolean
  mcpToolCalled?: string
  mcpToolSuccess?: boolean
}

function buildSendBody(
  text: string,
  sessionId: string | null,
  intention: string,
  uiResponse?: { ui_id: string; action_id: string; value: unknown }
): Record<string, unknown> {
  const body: Record<string, unknown> = { message: text, intention }
  if (sessionId) body.session_id = sessionId
  if (uiResponse) body.ui_response = uiResponse
  return body
}

export function useChat(options: UseChatOptions): UseChatReturn {
  const {
    intention,
    getJWT,
    endpoint = defaultEndpoint(),
    exportEndpoint = defaultExportEndpoint,
    storageKey = DEFAULT_STORAGE_KEY,
    storage,
    onAppletCreated,
    onSessionStarted,
    onSessionEnded,
    onTurnCompleted,
  } = options

  const resolvedStorage = storage ?? (typeof window !== "undefined" ? window.sessionStorage : undefined)
  const onAppletCreatedRef = useRef(onAppletCreated)
  useEffect(() => {
    onAppletCreatedRef.current = onAppletCreated
  }, [onAppletCreated])
  const onSessionStartedRef = useRef(onSessionStarted)
  useEffect(() => {
    onSessionStartedRef.current = onSessionStarted
  }, [onSessionStarted])
  const onSessionEndedRef = useRef(onSessionEnded)
  useEffect(() => {
    onSessionEndedRef.current = onSessionEnded
  }, [onSessionEnded])
  const onTurnCompletedRef = useRef(onTurnCompleted)
  useEffect(() => {
    onTurnCompletedRef.current = onTurnCompleted
  }, [onTurnCompleted])

  const [state, dispatch] = useReducer(reducer, INITIAL_STATE, initial => {
    const stored = readStored(resolvedStorage, storageKey)
    if (!stored) return initial
    if (stored.intention !== intention) return initial
    if (stored.settled) return initial
    return { ...initial, sessionId: stored.sessionId, phase: "connecting" }
  })

  const abortRef = useRef<AbortController | null>(null)
  const stateRef = useRef(state)
  stateRef.current = state

  const [initialAnalytics] = useState<SessionAnalytics>(() => {
    const restored = state.sessionId ? readAnalytics(resolvedStorage, state.sessionId) : null
    if (restored) return { ...restored, started: true, ended: false }
    return { ...freshAnalytics(), sessionId: state.sessionId, started: state.sessionId !== null }
  })
  const analyticsRef = useRef(initialAnalytics)
  const sessionStartedAtRef = useRef<number | null>(initialAnalytics.startedAt)
  const pendingTurnRef = useRef<PendingTurn | null>(null)

  const resolvePendingTurn = useCallback((userAction: TurnUserAction) => {
    const pending = pendingTurnRef.current
    pendingTurnRef.current = null
    const conversationId = analyticsRef.current.sessionId
    if (!pending || !conversationId) return
    onTurnCompletedRef.current?.({
      conversationId,
      turnNumber: pending.turnNumber,
      responseTimeMs: pending.responseTimeMs ?? Math.max(0, Date.now() - pending.promptAt),
      userAction,
      errorOccurred: pending.errorOccurred,
      mcpToolCalled: pending.mcpToolCalled,
      mcpToolSuccess: pending.mcpToolSuccess,
    })
  }, [])

  const openTurn = useCallback(() => {
    const analytics = analyticsRef.current
    pendingTurnRef.current = {
      turnNumber: analytics.turnsStarted,
      promptAt: Date.now(),
      responseTimeMs: null,
      errorOccurred: false,
    }
  }, [])

  const persistAnalytics = useCallback(() => {
    const analytics = analyticsRef.current
    if (!analytics?.sessionId || analytics.startedAt === null) return
    writeAnalytics(resolvedStorage, {
      sessionId: analytics.sessionId,
      startedAt: analytics.startedAt,
      turnsStarted: analytics.turnsStarted,
      aiResponses: analytics.aiResponses,
      goalAchieved: analytics.goalAchieved,
      errorBlocking: analytics.errorBlocking,
    })
  }, [resolvedStorage])

  const finalizeSession = useCallback(
    (exitMethod: SessionExitMethod, viaBeacon: boolean) => {
      const analytics = analyticsRef.current
      if (!analytics || !analytics.started || analytics.ended || !analytics.sessionId) return
      analytics.ended = true
      pendingTurnRef.current = null
      const endedAt = Date.now()
      const startedAt = analytics.startedAt ?? endedAt
      onSessionEndedRef.current?.({
        conversationId: analytics.sessionId,
        totalUserPrompts: analytics.turnsStarted,
        totalAiResponses: analytics.aiResponses,
        goalAchieved: analytics.goalAchieved,
        errorBlocking: analytics.errorBlocking,
        exitMethod,
        endedAtIso: new Date(endedAt).toISOString(),
        durationSeconds: Math.max(0, Math.round((endedAt - startedAt) / 1000)),
        viaBeacon,
      })
    },
    []
  )

  useEffect(() => {
    const handlePagehide = () => {
      finalizeSession(analyticsRef.current?.goalAchieved ? "natural_completion" : "navigation_away", true)
    }
    window.addEventListener("pagehide", handlePagehide)
    return () => window.removeEventListener("pagehide", handlePagehide)
  }, [finalizeSession])

  useEffect(() => {
    return () => finalizeSession(analyticsRef.current?.goalAchieved ? "natural_completion" : "navigation_away", true)
  }, [finalizeSession])

  // Persist session metadata as it changes.
  useEffect(() => {
    if (!state.sessionId) return
    writeStored(resolvedStorage, storageKey, {
      sessionId: state.sessionId,
      intention,
      settled: state.phase === "done" || state.phase === "idle",
    })
  }, [state.sessionId, state.phase, intention, resolvedStorage, storageKey])

  // Hydrate from /sessions/:id/export when we resumed an unfinished session.
  const hydratedRef = useRef(false)
  useEffect(() => {
    if (hydratedRef.current) return
    if (!state.sessionId || state.items.length > 0) return
    hydratedRef.current = true
    const sid = state.sessionId

    let cancelled = false
    ;(async () => {
      try {
        const token = await getJWT()
        const res = await fetch(exportEndpoint(sid), {
          headers: { Authorization: `Token jwt="${token}"` },
        })
        if (cancelled) return
        if (!res.ok) {
          clearStored(resolvedStorage, storageKey)
          clearAnalytics(resolvedStorage)
          analyticsRef.current = freshAnalytics()
          sessionStartedAtRef.current = null
          dispatch({ type: "RESET" })
          return
        }
        const data = (await res.json()) as unknown
        if (cancelled) return
        const { items } = parseHistoryExport(data)
        dispatch({ type: "HYDRATE", items, sessionId: sid })
      } catch {
        if (cancelled) return
        clearStored(resolvedStorage, storageKey)
        clearAnalytics(resolvedStorage)
        analyticsRef.current = freshAnalytics()
        dispatch({ type: "RESET" })
      }
    })()

    return () => {
      cancelled = true
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const handleSseEvent = useCallback(
    (sseEvent: SSEEvent) => {
      let parsed: Record<string, unknown>
      try {
        parsed = JSON.parse(sseEvent.data) as Record<string, unknown>
      } catch {
        return
      }

      if (sseEvent.event !== "session" && sseEvent.event !== "session_meta") {
        const pending = pendingTurnRef.current
        if (pending && pending.responseTimeMs === null) {
          pending.responseTimeMs = Math.max(0, Date.now() - pending.promptAt)
        }
      }

      switch (sseEvent.event) {
        case "session": {
          const id = pickString(parsed, "session_id", "id")
          if (id) {
            dispatch({ type: "SESSION", sessionId: id })
            const analytics = analyticsRef.current
            if (analytics && !analytics.started) {
              analytics.started = true
              analytics.sessionId = id
              analytics.startedAt = analytics.startedAt ?? sessionStartedAtRef.current ?? Date.now()
              persistAnalytics()
              onSessionStartedRef.current?.({
                conversationId: id,
                startedAtIso: new Date(analytics.startedAt).toISOString(),
              })
            } else if (analytics && analytics.sessionId === null) {
              analytics.sessionId = id
              persistAnalytics()
            }
          }
          return
        }
        case "session_meta":
          return
        case "status":
          dispatch({ type: "STATUS" })
          return
        case "token": {
          const text = pickString(parsed, "text", "token")
          if (text !== undefined) dispatch({ type: "TOKEN", text })
          return
        }
        case "tool_start": {
          const tool = pickString(parsed, "tool", "name") ?? ""
          const toolId = pickString(parsed, "tool_id", "tool") ?? tool
          dispatch({ type: "TOOL_START", toolId, tool })
          return
        }
        case "tool_done": {
          const toolId = pickString(parsed, "tool_id", "tool") ?? ""
          const status = pickString(parsed, "status")
          const error = pickString(parsed, "error")
          dispatch({ type: "TOOL_DONE", toolId, status, error })
          const pending = pendingTurnRef.current
          if (pending) {
            const tool = pickString(parsed, "tool") ?? toolId
            if (APPLET_TOOL_NAMES.has(tool) || !APPLET_TOOL_NAMES.has(pending.mcpToolCalled ?? "")) {
              pending.mcpToolCalled = tool
              pending.mcpToolSuccess = status === "success"
            }
          }
          if (status === "success" && APPLET_TOOL_NAMES.has(toolId)) {
            const analytics = analyticsRef.current
            if (analytics) {
              analytics.goalAchieved = true
              persistAnalytics()
            }
            const result = parsed.result
            const promptsToCreation = stateRef.current.items.reduce(
              (count, item) => (item.kind === "user" ? count + 1 : count),
              0
            )
            const startedAt = sessionStartedAtRef.current
            const conversationDurationSeconds = startedAt ? Math.max(0, Math.round((Date.now() - startedAt) / 1000)) : 0
            onAppletCreatedRef.current?.(result, {
              conversationId: stateRef.current.sessionId,
              promptsToCreation,
              conversationDurationSeconds,
            })
          }
          return
        }
        case "ui": {
          const id = pickString(parsed, "id") ?? ""
          const type = pickString(parsed, "type") ?? "unknown"
          const data = (parsed.data && typeof parsed.data === "object" ? parsed.data : {}) as Record<string, unknown>
          const actions = Array.isArray(parsed.actions) ? (parsed.actions as UIEvent["actions"]) : undefined
          const fallback_text = pickString(parsed, "fallback_text")
          dispatch({ type: "UI_EVENT", ui: { id, type, data, actions, fallback_text } })
          return
        }
        case "warning": {
          const usage = pickNumber(parsed, "usage") ?? 0
          const cap = pickNumber(parsed, "cap") ?? 0
          dispatch({ type: "WARNING", usage, cap })
          return
        }
        case "error": {
          const code = pickString(parsed, "code") ?? "unknown_error"
          const message = pickString(parsed, "message") ?? "Something went wrong"
          const recoverable = RECOVERABLE_ERROR_CODES.has(code)
          dispatch({ type: "ERROR_NOTICE", code, message, recoverable })
          if (pendingTurnRef.current) pendingTurnRef.current.errorOccurred = true
          if (TERMINAL_ERROR_CODES.has(code)) {
            clearStored(resolvedStorage, storageKey)
            const analytics = analyticsRef.current
            if (analytics) {
              analytics.errorBlocking = true
              persistAnalytics()
            }
          }
          return
        }
        case "done": {
          dispatch({ type: "DONE" })
          const analytics = analyticsRef.current
          if (analytics) {
            analytics.aiResponses += 1
            persistAnalytics()
          }
          return
        }
      }
    },
    [persistAnalytics, resolvedStorage, storageKey]
  )

  const runStream = useCallback(
    async (body: Record<string, unknown>) => {
      const controller = new AbortController()
      abortRef.current = controller
      dispatch({ type: "CONNECTING" })

      let token: string
      try {
        token = await getJWT()
      } catch {
        if (pendingTurnRef.current) pendingTurnRef.current.errorOccurred = true
        dispatch({
          type: "ERROR_NOTICE",
          code: "auth_failed",
          message: "Couldn't authenticate. Please refresh and try again.",
          recoverable: true,
        })
        abortRef.current = null
        return
      }

      try {
        for await (const sseEvent of streamSSE({
          url: endpoint,
          body,
          token,
          signal: controller.signal,
        })) {
          handleSseEvent(sseEvent)
        }
      } catch (err) {
        if (!(err instanceof DOMException && err.name === "AbortError")) {
          if (pendingTurnRef.current) pendingTurnRef.current.errorOccurred = true
          dispatch({
            type: "ERROR_NOTICE",
            code: "network_error",
            message: err instanceof Error ? err.message : "Connection failed",
            recoverable: true,
          })
        }
      } finally {
        if (abortRef.current === controller) abortRef.current = null
        const pending = pendingTurnRef.current
        if (pending && pending.responseTimeMs === null) {
          pending.responseTimeMs = Math.max(0, Date.now() - pending.promptAt)
        }
        dispatch({ type: "FINALIZE_INTERRUPTED" })
      }
    },
    [endpoint, getJWT, handleSseEvent]
  )

  const send = useCallback(
    async (text: string) => {
      const trimmed = text.trim()
      if (!trimmed) return
      if (abortRef.current) return
      resolvePendingTurn("sent_another_prompt")
      if (sessionStartedAtRef.current === null) sessionStartedAtRef.current = Date.now()
      const analytics = analyticsRef.current
      if (analytics) {
        if (analytics.startedAt === null) analytics.startedAt = sessionStartedAtRef.current
        analytics.turnsStarted += 1
        persistAnalytics()
      }
      openTurn()
      dispatch({ type: "USER_SEND", text: trimmed })
      const body = buildSendBody(trimmed, stateRef.current.sessionId, intention)
      await runStream(body)
    },
    [intention, openTurn, persistAnalytics, resolvePendingTurn, runStream]
  )

  const respondToUI = useCallback(
    async (uiId: string, actionId: string, value: unknown, displayText?: string, ingredients?: Record<string, string>) => {
      if (abortRef.current) return
      if (actionId === "close") {
        dispatch({ type: "UI_DISMISS", uiId })
        resolvePendingTurn("dismissed_card")
        return
      }
      // Permission cards (service connection, upgrade, applet preview) stay in the thread as a
      // disabled record of the choice instead of collapsing into a user bubble. Every other card
      // forwards its label as a user turn so the agent can acknowledge the pivot and keep the
      // conversation open. `ingredients` lets that user turn render its {{filter_code}} tokens as pills.
      const answered = stateRef.current.items.find(it => it.kind === "ui" && it.ui.id === uiId)
      const settles = answered?.kind === "ui" && SETTLING_CARD_TYPES.has(answered.ui.type)
      if (settles) {
        dispatch({ type: "UI_SETTLE", uiId, actionId, displayText })
      } else {
        dispatch({ type: "UI_RESPOND", uiId, displayText, ingredients })
      }
      resolvePendingTurn(actionId === "dismiss" ? "dismissed_card" : "advanced_via_card")
      if (sessionStartedAtRef.current === null) sessionStartedAtRef.current = Date.now()
      const analytics = analyticsRef.current
      if (analytics) {
        if (analytics.startedAt === null) analytics.startedAt = sessionStartedAtRef.current
        analytics.turnsStarted += 1
        persistAnalytics()
      }
      openTurn()
      // Valueless cards convey the user turn via `message`; the server prefers it over synthesizing from `value`.
      const message = value === undefined ? (displayText ?? "") : ""
      const body = buildSendBody(message, stateRef.current.sessionId, intention, {
        ui_id: uiId,
        action_id: actionId,
        value,
      })
      await runStream(body)
    },
    [intention, openTurn, persistAnalytics, resolvePendingTurn, runStream]
  )

  const stop = useCallback(() => {
    abortRef.current?.abort()
    abortRef.current = null
  }, [])

  const reset = useCallback(() => {
    abortRef.current?.abort()
    abortRef.current = null
    finalizeSession("chat_reset", false)
    pendingTurnRef.current = null
    analyticsRef.current = freshAnalytics()
    clearAnalytics(resolvedStorage)
    sessionStartedAtRef.current = null
    clearStored(resolvedStorage, storageKey)
    dispatch({ type: "RESET" })
    hydratedRef.current = true
  }, [finalizeSession, resolvedStorage, storageKey])

  return {
    items: state.items,
    phase: state.phase,
    sessionId: state.sessionId,
    capWarning: state.capWarning,
    send,
    stop,
    respondToUI,
    reset,
  }
}
